import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users, Search, Download, Printer, ChevronLeft } from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Resident Passes List Page
 */
export default function KyleRiseResidentPasses() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchPasses(); }, [page, statusFilter]);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/passes?pass_type=resident&page=${page}&limit=${limit}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (search) url += `&reg_number=${encodeURIComponent(search)}`;
      const res = await axios.get(url);
      setPasses(res.data.passes || []);
      setTotal(res.data.resident_count || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Pass ID", "Resident Name", "VRM", "Address", "Amount", "Status", "Valid Until", "Created"];
    const rows = passes.map(p => [p.pass_id, p.resident_name, p.reg_number, p.address || "", p.amount_display, p.status, p.valid_until ? new Date(p.valid_until).toLocaleDateString() : "", new Date(p.created_at).toLocaleDateString()]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `resident-passes-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kyle-rise-resident-passes">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/kyle-rise")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Resident Passes</h1><p className="text-sm text-gray-500">{total} passes</p></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Printer className="w-4 h-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchPasses()} placeholder="Search by VRM..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Resident</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">VRM</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Address</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Valid Until</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>)
              : passes.length === 0 ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No resident passes found</td></tr>)
              : passes.map((pass) => (
                <tr key={pass.pass_id} onClick={() => navigate(`/admin/kyle-rise/resident-passes/${pass.pass_id}`)} className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3"><p className="font-medium text-gray-900">{pass.resident_name}</p><p className="text-xs text-gray-500">{pass.pass_id}</p></td>
                  <td className="px-4 py-3"><span className="font-mono text-sm bg-yellow-100 px-2 py-1 rounded">{pass.reg_number}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{pass.address || "-"}</td>
                  <td className="px-4 py-3 text-sm font-medium">{pass.amount_display}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${pass.status === "active" ? "bg-green-100 text-green-700" : pass.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"}`}>{pass.status}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-500">{pass.valid_until ? new Date(pass.valid_until).toLocaleDateString() : "-"}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(pass.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
}
